import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AzureMonitoringService } from 'app/core/services/AzureMonitoringService';
import { OrderRestService } from 'app/core/services/rest/order.rest.service';
import { RelocationRestService } from 'app/core/services/rest/relocation.rest.service';
import { TemplateRestService } from 'app/core/services/rest/template.rest.service';
import { AbstractEffects } from 'app/core/store/AbstractEffects';
import { RootState } from 'app/core/store/index';
import { Observable, of } from 'rxjs';
import * as actions from './calendar-orders.actions';
import { CalendarOrdersLoadAction } from './calendar-orders.actions';
import { catchError, debounceTime, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationService } from 'app/core/services/notification.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SessionState } from 'app/core/store/session/session.interface';

@Injectable({ providedIn: 'root' })
export class CalendarOrdersEffects extends AbstractEffects {
  loadOrders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<CalendarOrdersLoadAction>(actions.CALENDAR_ORDERS_LOAD),
      withLatestFrom(
        this.store.select(state => state.session),
        (action, session) => {
          return { action, session };
        }
      ),
      switchMap(({ action, session }: { action: CalendarOrdersLoadAction; session: SessionState }) => {
        // Register to this event, unregister previous if new event occurs.
        let customers = null;
        if (!action.payload.filter.orderTypes || action.payload.filter.orderTypes.length === 0) {
          return of(
            new actions.CalendarOrdersLoadSuccessAction({
              dates: {
                legend: {},
                dates: [],
              },
              isSuperUser: session.userContext.isSuperUser,
              selectedCustomer: session.selectedCustomer.data,
              customers: session.customerPermissions.data.queryOrGetOrder,
            })
          );
        } else {
          customers = session.customerPermissions.data.queryOrGetOrder;
          return this.orderRestService
            .getOrdersForCalendar({ ...action.payload.filter }, customers) // Request orders
            .pipe(
              map(daysWithFlordersInfoTO => {
                return new actions.CalendarOrdersLoadSuccessAction({
                  dates: daysWithFlordersInfoTO,
                  isSuperUser: session.userContext.isSuperUser,
                  selectedCustomer: session.selectedCustomer.data,
                  customers: session.userContext.customers,
                });
              }),
              catchError((err: HttpErrorResponse) => {
                if (err.error?.Message?.indexOf('No orders found for given criteria') >= 0) {
                  return of(
                    new actions.CalendarOrdersLoadSuccessAction({
                      dates: {
                        dates: [],
                        legend: {},
                      },
                      isSuperUser: session.userContext.isSuperUser,
                      selectedCustomer: session.selectedCustomer.data,
                      customers: session.userContext.customers,
                    })
                  );
                } else {
                  return this.handleFail(err, 'ERRORS.FILTER_FAILED.TITLE', [of(new actions.CalendarOrdersLoadFailAction(err))]);
                }
              })
            );
        }
      })
    )
  );
  requestOrderDeliveryDatesForTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.CALENDAR_ORDERS_REQUEST_ORDER_DELIVERY_DATES),
      debounceTime(50),
      withLatestFrom(this.store, (action, state) => {
        return { state };
      }),
      switchMap(({ state }) => {
        // template given
        return this.templateRestService.getTemplateOrderDeliveryDates(state.calendar.orders.selectedTemplate, state.calendar.orders.UI.startDate).pipe(
          map(result => new actions.CalendarOrdersRequestOrderDeliveryDatesSuccessAction(result)),
          catchError(err => this.handleFail(err, 'ERRORS.TEMPLATE.CHECK_DELIVERY_DATES', [of(new actions.CalendarOrdersRequestOrderDeliveryDatesFailAction(err))]))
        );
      })
    )
  );

  constructor(
    private store: Store<RootState>,
    azureMonitoringService: AzureMonitoringService,
    private actions$: Actions,
    private orderRestService: OrderRestService,
    private relocationRestService: RelocationRestService,
    private templateRestService: TemplateRestService,
    translateService: TranslateService,
    notificationService: NotificationService
  ) {
    super(translateService, azureMonitoringService, notificationService);
  }
}
